import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {StationModel} from "../../shared/models/station.model";
import {MatAutocomplete} from "@angular/material/autocomplete";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Subscription} from "rxjs";
import {FlatTreeControl} from "@angular/cdk/tree";
import {MatTreeFlatDataSource, MatTreeFlattener} from "@angular/material/tree";
import {VariablesCategoriesService} from "../../shared/services/variables-categories.service";
import {GraphService} from "../../shared/services/graph.service";
import {NgbCalendar} from "@ng-bootstrap/ng-bootstrap";
import {FlatNode, SimpleNode} from "../../shared/models/simple-node.model";
import {StationsService} from "../../shared/services/stations.service";
import {AuthService} from "../../shared/services/auth.service";
import {DataAvailabilityService} from "../../shared/services/data-availability.service";
import {CollectionModel} from "../../shared/models/collection.model";
import {VariablesPublicNamesService} from "../../shared/services/variables-public-names.service";
import {VariablePublicNamesMap} from "../../shared/utils/variable-display-name";
import {take} from "rxjs/operators";

@Component({
  selector: 'app-graph-form',
  templateUrl: './graph-form.component.html',
  styleUrls: ['./graph-form.component.scss']
})
export class SelectGraphForm implements OnInit, OnDestroy {
  @Input() componentContext: 'graph-page' | 'dataset-page';
  @Input() collection: CollectionModel | null; // Used in dataset page context
  @ViewChild('auto') autocomplete: MatAutocomplete;
  @ViewChild('autoTreeInput') autoInput: ElementRef;
  stationsSubscription: Subscription;
  variablesCategoriesSubscription: Subscription;
  collectionDataAvailabilitySubscription: Subscription;
  availableDataForSelectedVariable: any;
  graphTypeSubscription: Subscription;
  userSubscription: Subscription;
  graphQuerySizeSubscription: Subscription;
  stationFilterSubscription: Subscription;
  formValueChangesSubscription: Subscription;
  isLoggedIn: boolean;
  form: FormGroup;
  minDate: Date;
  flatNodeMap = new Map<FlatNode, SimpleNode>();
  nestedNodeMap = new Map<SimpleNode, FlatNode>();
  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<SimpleNode, FlatNode>;
  variablesCategories: MatTreeFlatDataSource<SimpleNode, FlatNode>;
  fullStationsList: StationModel[] = [];
  fromDate = null;
  toDate = null;
  selectedStations: string[] = [];
  selectedDatasetPageVariables: string[] = [];
  availableSingleDepths: number[] = [];
  availableDateMin: any;
  availableDateMax: any;

  @Input() datasetPageVariablesList: string[] = [];
  selectedDatasetPageVariable: string;

  filteredStations: StationModel[] = [];
  availableStationsForSelectedVariable: StationModel[] = []; // full list
  stationFilterControl = new FormControl('');
  regionFilter: 'kinneret' | 'med' | null = null;
  formErrorMessage: string | null = null;
  /**
   * #23 — configured public variable names, keyed by technical identifier. Fetched per
   * component init (no app-lifetime cache), so an Admin rename is picked up on the next
   * page load without an application reload.
   */
  publicNamesMap: VariablePublicNamesMap = {};
  private rawVariableCategories: SimpleNode[] = [];

  constructor(
    private calendar: NgbCalendar,
    private variablesCategoriesService: VariablesCategoriesService,
    private dataAvailabilityService: DataAvailabilityService,
    private stationsService: StationsService,
    private graphService: GraphService,
    private authService: AuthService,
    private variablesPublicNamesService: VariablesPublicNamesService
  ) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<FlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.variablesCategories = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
    this.fromDate = this.calendar.getToday();
  }

  ngOnInit() {
    this.getPublicVariableNames();
    this.getStationsList();
    this.subscribeToUserSubject();

    if (this.componentContext === 'graph-page') {
      this.variablesCategoriesService.getVariablesCategories();
      this.subscribeToVariableCategories();
    }
    // this.loadDateRangeFromCookie();

    this.stationFilterSubscription = this.stationFilterControl.valueChanges.subscribe((value: string) => {
      this.filterStations(value || '');
    });
  }

  /**
   * #23 — load the configured public names used to label the variable selector, the tree
   * leaves and the selected-variable chips. `take(1)` per init; deliberately no shared
   * cache, so the labels are as fresh as the page.
   */
  private getPublicVariableNames(): void {
    this.variablesPublicNamesService.getVariablesPublicNames()
      .pipe(take(1))
      .subscribe(map => this.publicNamesMap = map ?? {});
  }

  private subscribeToUserSubject(): void {
    this.userSubscription = this.authService.userSubject.subscribe(user => {
      this.isLoggedIn = !!user;
      this.setForm();
    })
  }

  private subscribeToVariableAvailableData(req: any): void {
    this.collectionDataAvailabilitySubscription = this.dataAvailabilityService.collectionAvailableDataByCollectionVariables(req).subscribe((availableDataForSelectedVariable: any) => {
      this.availableDataForSelectedVariable = availableDataForSelectedVariable;
      const allStations = new Set<string>();

      availableDataForSelectedVariable.forEach((collectionObj: any) => {
        // Loop through variable keys (excluding `_id`)
        Object.keys(collectionObj).forEach(key => {
          if (key === '_id') return;

          const records = collectionObj[key];
          records.forEach((record: any) => {
            // add station to allStations set
            allStations.add(record.station);
          });
        });
      });

      // Filter station list to only those found in response, respecting region
      this.availableStationsForSelectedVariable = this.fullStationsList.filter(station =>
        allStations.has(station.title) && this.matchesRegion(station)
      );

      this.filteredStations = [...this.availableStationsForSelectedVariable];

      this.form.controls['station'].enable();
    });
  }

  filterStations(searchText: string): void {
    const lower = searchText.toLowerCase();
    this.filteredStations = this.availableStationsForSelectedVariable.filter(station =>
      station.title.toLowerCase().includes(lower)
    );
  }

  toggleStation(stationTitle: string): void {
    const index = this.selectedStations.findIndex((s) => s === stationTitle);
    if (index >= 0) {
      this.selectedStations.splice(index, 1); // Remove if already selected
    } else {
      this.selectedStations.push(stationTitle); // Add if not selected
    }

    this.form.controls.station.setValue(this.selectedStations);

    this.onSelectStation()
  }

  private onSelectStation(): void {
    if (!this.availableDataForSelectedVariable || !this.selectedStations.length) {
      return;
    }

    let globalFromDate: Date = null;
    let globalToDate: Date = null;
    const depthSet = new Set<number>();

    this.availableDataForSelectedVariable.forEach((collectionObj: any) => {
      Object.keys(collectionObj).forEach((key) => {
        if (key === '_id') return;

        const records = collectionObj[key] as any[];
        records.forEach(record => {
          if (this.selectedStations.includes(record.station)) {
            const from = new Date(record.fromDate);
            const to = new Date(record.toDate);

            if (!globalFromDate || from < globalFromDate) {
              globalFromDate = from;
            }
            if (!globalToDate || to > globalToDate) {
              globalToDate = to;
            }

            // Collect depths
            if (Array.isArray(record.depths)) {
              record.depths.forEach((d: number) => depthSet.add(d));
            }
          }
        });
      });
    });

    // Set available date range
    this.availableDateMin = globalFromDate;
    this.availableDateMax = globalToDate;

    if (globalFromDate && globalToDate) {
      this.form.controls['fromDate'].enable();
      this.form.controls['toDate'].enable();
      this.form.controls['depthMode'].enable();
      this.form.controls['depthAggregation'].enable();
      this.form.controls['timeSteps'].enable();
      this.form.controls['dataType'].enable();
      this.form.controls['singleDepth'].enable();
      this.form.controls['fromDepth'].enable();
      this.form.controls['toDepth'].enable();
    } else {
      this.form.controls['fromDate'].disable();
      this.form.controls['toDate'].disable();
    }

    // Set available depths
    this.availableSingleDepths = Array.from(depthSet).sort((a, b) => a - b);

    // Optionally reset depth selection if current values are no longer valid
    this.form.controls['singleDepth'].setValue(null);
    this.form.controls['fromDepth'].setValue(null);
    this.form.controls['toDepth'].setValue(null);
  }

  isSelected(station: StationModel): boolean {
    return this.selectedStations.some((s) => s === station.title);
  }

  private getStationsList(): void {
    this.stationsSubscription = this.stationsService.getStations().subscribe(stations => {
      this.fullStationsList = stations;
    })
  }

  private loadDateRangeFromCookie(): void {
    let dateRange: any = this.graphService.getLocalStorageSelectedVariables('iolrSelectedDateRange');
    if (dateRange) {
      dateRange = JSON.parse(dateRange);
      this.fromDate = dateRange.fromDate;
      this.toDate = dateRange.toDate;
    }
  }

  private subscribeToVariableCategories(): void {
    this.datasetPageVariablesList = [];
    this.variablesCategoriesSubscription = this.variablesCategoriesService.variablesCategoriesSubject.subscribe((data) => {
      if (data.length > 0) {
        const visibleData = data.filter(item => item.item !== 'spatial');
        this.rawVariableCategories = visibleData;
        this.applyRegionFilter();
      }
    });
  }

  onRegionChange(region: 'kinneret' | 'med'): void {
    this.regionFilter = region;
    this.applyRegionFilter();
    // Clear variable and station selections — they may not belong to the new region
    this.selectedStations = [];
    this.availableStationsForSelectedVariable = [];
    this.filteredStations = [];
    this.availableDataForSelectedVariable = null;
    if (this.form) {
      this.form.patchValue({ station: [], column: [], collectionName: null, category: null });
      this.form.controls['station'].disable();
    }
  }

  private applyRegionFilter(): void {
    if (!this.regionFilter) {
      this.variablesCategories.data = [];
      return;
    }
    this.variablesCategories.data = this.filterTreeByRegion(this.rawVariableCategories, this.regionFilter);
  }

  private filterTreeByRegion(nodes: SimpleNode[], region: 'kinneret' | 'med'): SimpleNode[] {
    return nodes
      .map(cat => ({
        ...cat,
        children: cat.children?.filter(col =>
          region === 'med' ? col.is_med === true : col.is_med !== true
        ) || []
      }))
      .filter(cat => (cat.children?.length ?? 0) > 0);
  }

  private matchesRegion(station: StationModel): boolean {
    if (this.regionFilter === 'med') return station.is_med === true;
    if (this.regionFilter === 'kinneret') return station.is_med !== true;
    return true;
  }

  /*private loadSelectedStationsFromCookie(): void {
    const iolrSelectedStations = this.cookieService.get('iolrSelectedStations');
    this.selectedStationsList = iolrSelectedStations ? JSON.parse(iolrSelectedStations) : null;
  }*/

  private setForm(): void {
    const today = new Date();
    this.minDate = this.isLoggedIn ? null : new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());

    this.form = new FormGroup({
      fromDate: new FormControl({value: null, disabled: true}, [
        // Validators.required,
        this.validateMinDate.bind(this)
      ]),
      toDate: new FormControl({value: null, disabled: true}), // , Validators.required
      station: new FormControl({value: [], disabled: true}), // , Validators.required
      category: new FormControl({value: [], disabled: true}, Validators.required),
      collectionName: new FormControl({value: [], disabled: true}, Validators.required),
      collectionPublicName: new FormControl([]), // hidden
      column: new FormControl({value: [], disabled: true}, Validators.required),
      depthMode: new FormControl({value: 'singleDepth', disabled: true}, Validators.required),
      singleDepth: new FormControl({value: null, disabled: true}),
      fromDepth: new FormControl({value: null, disabled: true}),
      toDepth: new FormControl({value: null, disabled: true}),
      depthAggregation: new FormControl({value: null, disabled: true}),
      timeSteps: new FormControl({value: null, disabled: true}),
      dataType: new FormControl({value: 'raw', disabled: true}, Validators.required),
    });

    // Clear the validation alert as soon as the user changes any field, so a stale
    // "Please select ..." message doesn't linger after its condition is satisfied.
    // setForm() can run more than once (userSubject re-emits), so drop the old
    // subscription before wiring a new one to the freshly created form.
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
    this.formValueChangesSubscription = this.form.valueChanges.subscribe(() => {
      if (this.formErrorMessage) {
        this.formErrorMessage = null;
      }
    });
  }

  validateMinDate(control: FormControl) {
    if (control.value && new Date(control.value) < this.minDate) {
      return {minDate: true}; // Error key
    }
    return null;
  }

  onReset(): void {
    this.form.reset();
    this.formErrorMessage = null;
    if (this.autoInput) {
      this.autoInput.nativeElement.value = '';
    }
    this.graphService.graphVariablesSubject.next([]);
    this.treeControl.collapseAll();
    this.selectedStations = [];
    this.selectedDatasetPageVariables = [];
    const deleteSelectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext)
    this.graphService.deleteLocalStorageSelectedVariables(deleteSelectedVariablesKey);

    this.form.controls.column.setValue([]);
    this.form.controls.station.setValue([]);
    this.form.controls.fromDate.setValue(null);
    this.form.controls.toDate.setValue(null);
    this.form.controls.depthMode.setValue('singleDepth');
    this.form.controls.singleDepth.setValue(null);
    this.form.controls.fromDepth.setValue(null);
    this.form.controls.toDepth.setValue(null);
    this.form.controls.depthAggregation.setValue(null);
    this.form.controls.timeSteps.setValue(null);
    this.form.controls.dataType.setValue('raw');

    this.form.controls['station'].disable();
    this.form.controls['fromDate'].disable();
    this.form.controls['toDate'].disable();
    this.form.controls['depthMode'].disable();
    this.form.controls['dataType'].disable();
    this.form.controls['singleDepth'].disable();
    this.form.controls['fromDepth'].disable();
    this.form.controls['toDepth'].disable();
    this.graphService.graphVariablesSubject.next(null)
  }

  onSubmit(): void {
    // Validate the form and surface a clear, user-visible message on failure
    // instead of failing silently (the form previously only logged to console).
    const validationError = this.validateForm();
    if (validationError || this.form.invalid) {
      // Mark all controls as touched so per-field errors also show
      this.form.markAllAsTouched();
      this.formErrorMessage = validationError ?? 'Please complete all required fields before visualizing.';
      return;
    }

    // Clear any previous error now that the submission is valid
    this.formErrorMessage = null;

    const formControls = this.form.controls;

    const selectedVariablesToStore: any[] = []; // This array will hold all the correctly structured items

    // Get current values from form controls
    const selectedStationTitles = formControls['station'].value as string[];
    const selectedColumns = formControls['column'].value as string[];
    const selectedCategories = formControls['category'].value as string[];
    const selectedCollectionNames = formControls['collectionName'].value as string[];
    const selectedCollectionPublicNames = formControls['collectionPublicName'].value as string[];

    // Iterate through each selected station and each selected variable
    selectedStationTitles.forEach((stationTitle: string) => {
      selectedColumns.forEach((variable: string, index: number) => {
        const item = {
          ...this.form.value,
          station: stationTitle,
          column: variable,
          category: selectedCategories[index],
          collectionName: selectedCollectionNames[index],
          collectionPublicName: selectedCollectionPublicNames && selectedCollectionPublicNames.length > 0 ? selectedCollectionPublicNames[index] : null
        };
        selectedVariablesToStore.push(item);
      });
    });

    // Retrieve existing data from local storage to append new selections
    const selectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    const existingVariables = this.graphService.getLocalStorageSelectedVariables(selectedVariablesKey) || [];

    // Combine existing variables with the newly generated ones and save back to local storage
    this.graphService.setLocalStorageSelectedVariables(selectedVariablesKey, [...existingVariables, ...selectedVariablesToStore]);

    // --- Reset the form and update the UI after successful submission ---

    // Reset display variables first for immediate UI feedback
    this.selectedDatasetPageVariable = null;
    this.selectedDatasetPageVariables = [];
    this.selectedStations = []; // Clear selected stations display (chips)

    // Reset relevant form controls to their initial/default states
    // Note: We don't use form.reset() directly here as it would clear disabled states
    formControls['column'].setValue([]);
    formControls['station'].setValue([]); // Clear the form control for stations
    formControls['fromDate'].setValue(null);
    formControls['toDate'].setValue(null);
    formControls['depthMode'].setValue('singleDepth'); // Reset to default depth mode
    formControls['singleDepth'].setValue(null);
    formControls['fromDepth'].setValue(null);
    formControls['toDepth'].setValue(null);
    formControls['depthAggregation'].setValue(null);
    formControls['timeSteps'].setValue(null);
    formControls['dataType'].setValue('raw'); // Reset to default data type

    // Re-disable controls if needed, reflecting initial state
    // This is important if you disable controls when no variable/station is selected
    // The 'onSelectStation' would enable them again if selections are made.
    formControls['station'].disable();
    formControls['fromDate'].disable();
    formControls['toDate'].disable();
    formControls['depthMode'].disable();
    formControls['dataType'].disable();
    formControls['singleDepth'].disable();
    formControls['fromDepth'].disable();
    formControls['toDepth'].disable();
    formControls['depthAggregation'].disable();

    this.sendVariableData();
  }

  /**
   * Returns a user-friendly message describing the first missing/invalid field,
   * or null when the form is ready to be submitted. Mirrors the conditional
   * depth requirements enforced by the template.
   */
  private validateForm(): string | null {
    const c = this.form.controls;

    if (!c['column'].value?.length) {
      return 'Please select at least one variable before visualizing.';
    }
    if (!c['station'].value?.length) {
      return 'Please select at least one station before visualizing.';
    }
    if (!c['fromDate'].value || !c['toDate'].value) {
      return 'Please select a date range before visualizing.';
    }
    if (c['fromDate'].hasError('minDate')) {
      return `Start date cannot be earlier than ${this.minDate ? this.minDate.toLocaleDateString() : 'the allowed range'}.`;
    }
    if (!c['depthMode'].value) {
      return 'Please select a depth mode before visualizing.';
    }

    if (c['depthMode'].value === 'singleDepth') {
      if (c['singleDepth'].value === null || c['singleDepth'].value === undefined) {
        return 'Please select a depth before visualizing.';
      }
    } else if (c['depthMode'].value === 'rangeDepth') {
      if (c['fromDepth'].value === null || c['fromDepth'].value === undefined ||
        c['toDepth'].value === null || c['toDepth'].value === undefined) {
        return 'Please select both a "from" and "to" depth before visualizing.';
      }
      if (!c['depthAggregation'].value) {
        return 'Please select a depth aggregation method before visualizing.';
      }
      if (c['dataType'].value !== 'raw' && !c['timeSteps'].value) {
        return 'Please select a time step for the chosen aggregation before visualizing.';
      }
    }

    return null;
  }

  private sendVariableData(): void {
    const selectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    const variables = this.graphService.getLocalStorageSelectedVariables(selectedVariablesKey)
    this.graphService.graphVariablesSubject.next(variables)
  }

  private setFormWithLastSubmittedValues(): void {
    const localDataKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    const localData = this.graphService.getLocalStorageSelectedVariables(localDataKey)
    if (localData) {
      const selectedVariables = JSON.parse(localData);
      if (selectedVariables.length > 0) {
        const lastSelectedStation = selectedVariables[selectedVariables.length - 1].station;
        this.toggleStation(lastSelectedStation)

        const lastFromDateValue = selectedVariables[selectedVariables.length - 1].fromDate;
        this.form.controls.fromDate.setValue(lastFromDateValue);

        const lastToDateValue = selectedVariables[selectedVariables.length - 1].toDate;
        this.form.controls.toDate.setValue(lastToDateValue);

        const lastDepthMode = selectedVariables[selectedVariables.length - 1].depthMode;
        this.form.controls.depthMode.setValue(lastDepthMode);

        const lastSingleDepth = selectedVariables[selectedVariables.length - 1].singleDepth;
        this.form.controls.singleDepth.setValue(lastSingleDepth);

        const lastFromDepth = selectedVariables[selectedVariables.length - 1].fromDepth;
        this.form.controls.fromDepth.setValue(lastFromDepth);

        const lastToDepth = selectedVariables[selectedVariables.length - 1].toDepth;
        this.form.controls.toDepth.setValue(lastToDepth);

        const lastDepthAggregation = selectedVariables[selectedVariables.length - 1].depthAggregation;
        this.form.controls.depthAggregation.setValue(lastDepthAggregation);
      }
    }
  }

  private setVariablesInLocalStorage(item: any): void {
    const selectedVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    const selectedVariables = this.graphService.getLocalStorageSelectedVariables(selectedVariablesKey);
    selectedVariables.push(item);
    this.graphService.setLocalStorageSelectedVariables(selectedVariablesKey, selectedVariables);
  }

  private getLevel = (node: FlatNode) => node.level;

  private isExpandable = (node: FlatNode) => node.expandable;

  private getChildren = (node: SimpleNode): SimpleNode[] => node.children;

  hasChild = (_: number, _nodeData: FlatNode) => _nodeData.expandable;

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  private transformer = (node: SimpleNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item ? existingNode : new FlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.category = node.category ? node.category : null;
    flatNode.collectionName = node.collectionName ? node.collectionName : null;
    flatNode.collectionPublicName = node.collectionPublicName ? node.collectionPublicName : null;
    flatNode.level = level;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  opened = () => {
    let inputWidth = this.autoInput.nativeElement.getBoundingClientRect().width;
    setTimeout(() => {
      let panel = (this.autocomplete.panelWidth as any).panel?.nativeElement;
      if (!panel) return;
      panel.style.minWidth = inputWidth + 'px';
    });
  };

  isVariableSelected(node: FlatNode): boolean {
    const indexOfSelectedColumn = this.form.controls.column.value?.indexOf(node.item);
    if (indexOfSelectedColumn >= 0) {
      const selectedCategory = this.form.controls.category.value[indexOfSelectedColumn];
      const selectedCollectionName = this.form.controls.collectionName.value[indexOfSelectedColumn];
      return !(node.category !== selectedCategory || node.collectionName !== selectedCollectionName);
    }
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: FlatNode): void {
    const selectedColumns = this.form.controls.column.value || [];
    const selectedCategories = this.form.controls.category.value || [];
    const selectedCollectionNames = this.form.controls.collectionName.value || [];
    const selectedCollectionPublicNames = this.form.controls.collectionPublicName.value || [];

    const index = selectedColumns.indexOf(node.item);

    if (index >= 0) {
      // If the variable is already selected, remove it
      selectedColumns.splice(index, 1);
      selectedCategories.splice(index, 1);
      selectedCollectionNames.splice(index, 1);
      selectedCollectionPublicNames.splice(index, 1);
    } else {
      // If the variable is not selected, add it
      selectedColumns.push(node.item);
      selectedCategories.push(node.category);
      selectedCollectionNames.push(node.collectionName);
      selectedCollectionPublicNames.push(node.collectionPublicName);
    }

    // Update the form control
    this.form.controls.column.setValue([...selectedColumns]);
    this.form.controls.category.setValue([...selectedCategories]);
    this.form.controls.collectionName.setValue([...selectedCollectionNames]);
    this.form.controls.collectionName.setValue([...selectedCollectionNames]);
    this.form.controls.collectionPublicName.setValue([...selectedCollectionPublicNames]);

    const requestCollectionVariableAvailableData: { [collectionName: string]: string[] } = {
      [node.collectionName]: [node.item]
    };

    this.subscribeToVariableAvailableData(requestCollectionVariableAvailableData)
  }

  onSelectDatasetPageVariable(variable: string[]): void {
    this.selectedDatasetPageVariables = variable;

    let selectedColumns = this.form.controls.column.value || [];
    const selectedCategories = this.form.controls.category.value || [];
    const selectedCollectionNames = this.form.controls.collectionName.value || [];

    const index = selectedColumns.indexOf(variable);

    if (index >= 0) {
      // If the variable is already selected, remove it
      selectedColumns.splice(index, 1);
      selectedCategories.splice(index, 1);
      selectedCollectionNames.splice(index, 1);
    } else {
      // If the variable is not selected, add it
      selectedColumns = variable;
      selectedCategories.push(this.collection.category);
      selectedCollectionNames.push(this.collection.title);
    }

    // Update the form control
    this.form.controls.column.setValue([...selectedColumns]);
    this.form.controls.category.setValue([...selectedCategories]);
    this.form.controls.collectionName.setValue([...selectedCollectionNames]);

    const requestCollectionVariableAvailableData: { [collectionName: string]: string[] } = {
      [this.collection.title]: variable
    };

    this.subscribeToVariableAvailableData(requestCollectionVariableAvailableData)
  }

  removeDatasetPageVariable(index: number, item: string): void {
    const i = this.selectedDatasetPageVariables.indexOf(item);

    if (i >= 0) {
      this.selectedDatasetPageVariables.splice(i, 1);

      // Create a new array instance to trigger change detection
      this.selectedDatasetPageVariables = [...this.selectedDatasetPageVariables];

      this.form.controls.column.setValue(this.selectedDatasetPageVariables);
    }

    if (this.selectedDatasetPageVariables.length === 0) {
      this.onReset()
    }
  }


  removeStation(stationTitle: string): void {
    const index = this.selectedStations.indexOf(stationTitle);

    if (index >= 0) {
      this.selectedStations.splice(index, 1);
    }

    this.form.controls.station.setValue([...this.selectedStations]);

    this.onSelectStation()
  }

  removeVariable(item: string): void {
    const selectedColumns = this.form.controls.column.value || [];
    const index = selectedColumns.indexOf(item);

    if (index >= 0) {
      selectedColumns.splice(index, 1);
      this.form.controls.column.setValue([...selectedColumns]);
    }
    if (index === 0) {
      this.onReset()
    }
  }

  filterChanged(e: any) {
    const filterText = e.target.value;
    this.variablesCategoriesService.filter(filterText);
    if (filterText) {
      this.treeControl.expandAll();
    } else {
      this.treeControl.collapseAll();
    }
  }

  onDepthModeChange(): void {
    switch (this.form.controls.depthMode.value) {
      case 'singleDepth':
        this.form.controls.fromDepth.setValue(null);
        this.form.controls.toDepth.setValue(null);
        this.form.controls.depthAggregation.setValue(null);
        break;
      default:
        this.form.controls.singleDepth.setValue(null);
    }
  }

  ngOnDestroy(): void {
    if (this.stationsSubscription) {
      this.stationsSubscription.unsubscribe();
    }
    if (this.variablesCategoriesSubscription) {
      this.variablesCategoriesSubscription.unsubscribe();
    }
    if (this.collectionDataAvailabilitySubscription) {
      this.collectionDataAvailabilitySubscription.unsubscribe();
    }
    if (this.graphTypeSubscription) {
      this.graphTypeSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.graphQuerySizeSubscription) {
      this.graphQuerySizeSubscription.unsubscribe();
    }
    if (this.stationFilterSubscription) {
      this.stationFilterSubscription.unsubscribe();
    }
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
  }
}
